// frontend/shared-ui/src/forms/schemas/accessory.js

// ─── Fiber standards ─────────────────────────────────────────────────────
const FIBER_STANDARDS = [
    { value: 'G.652.D', label: 'G.652.D — Standard single-mode' },
    { value: 'G.655', label: 'G.655 — NZ-DSF (for DWDM)' },
    { value: 'G.657.A1', label: 'G.657.A1 — Bend-insensitive (10mm)' },
    { value: 'G.657.A2', label: 'G.657.A2 — Bend-insensitive (7.5mm)' },
    { value: 'G.657.B3', label: 'G.657.B3 — Bend-insensitive (5mm)' },
    { value: 'OM1', label: 'OM1 — 62.5µm multimode' },
    { value: 'OM2', label: 'OM2 — 50µm multimode' },
    { value: 'OM3', label: 'OM3 — 50µm laser-optimized' },
    { value: 'OM4', label: 'OM4 — 50µm extended reach' },
    { value: 'OM5', label: 'OM5 — Wideband multimode' },
];

const CONNECTOR_OPTIONS = ['SC', 'LC', 'FC', 'ST', 'E2000', 'MPO', 'MTP'];

// Sentinel: "no Side B" — only THIS value means pigtail (single-ended)
const PIGTAIL_VALUE = 'Pigtail (no end)';

const POLISH_OPTIONS = [
    { value: 'APC', label: 'APC (Green)' },
    { value: 'UPC', label: 'UPC (Blue)' },
    { value: 'PC', label: 'PC' },
];

const ACCESSORY_TYPES = [
    'Connector',
    'Adapter',
    'Pigtail',
    'Patch Cord',
    'Splice Protector',
    'Cleaning Supply',
    'Field Assembly Connector',
    'Other',
];

const ACCESSORY_TYPE_CODES = {
    Connector: 'CONN',
    Adapter: 'ADPT',
    Pigtail: 'PIGT',
    'Patch Cord': 'PATC',
    'Splice Protector': 'SPLPR',
    'Cleaning Supply': 'CLEAN',
    'Field Assembly Connector': 'FAC',
    Other: 'ACC',
};

const CORE_TYPE_CODES = {
    Simplex: 'SX',
    Duplex: 'DX',
    Quad: 'QX',
};

const FIBER_MODE_CODES = {
    Singlemode: 'SM',
    'Multimode OM1': 'OM1',
    'Multimode OM2': 'OM2',
    'Multimode OM3': 'OM3',
    'Multimode OM4': 'OM4',
    'Multimode OM5': 'OM5',
};

// ─── Field-Assembly-specific options ─────────────────────────────────────
const ASSEMBLY_MECHANISM_OPTIONS = [
    { value: 'Lever Lock', label: 'Lever Lock Type' },
    { value: 'Screw Compression', label: 'Screw Compression Type' },
    { value: 'Mini Push-Lock', label: 'Mini Push-Lock Type' },
    { value: 'Direct Assembly', label: 'Direct Assembly' },
    { value: 'Embedded', label: 'Embedded (factory pre-assembled)' },
];

const ASSEMBLY_FAMILY_OPTIONS = [
    { value: 'Field Assembly Fast Connector', label: 'Field Assembly Fast Connector' },
    { value: 'ESC250D', label: 'ESC250D (embedded)' },
    { value: 'ESC250', label: 'ESC250' },
    { value: 'ESC250L', label: 'ESC250L' },
    { value: 'Other', label: 'Other (specify in notes)' },
];

const ASSEMBLY_LENGTH_OPTIONS = [
    { value: '50.0', label: '50.0 mm' },
    { value: '52.0', label: '52.0 mm' },
    { value: '53.0', label: '53.0 mm' },
    { value: '55.0', label: '55.0 mm' },
    { value: '60.0', label: '60.0 mm' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────
function endLabel(type, polish) {
    if (!type) return '';
    return polish ? `${type}/${polish}` : type;
}

function isSideBPigtail(v) {
    return v && v.connectorTypeB === PIGTAIL_VALUE;
}

function hasSideBConnector(v) {
    return v && v.connectorTypeB && v.connectorTypeB !== PIGTAIL_VALUE;
}

function isFieldAssembly(v) {
    return v && v.accessoryType === 'Field Assembly Connector';
}

function modeShort(v) {
    return FIBER_MODE_CODES[v.fiberMode] || '';
}

function assemblyLengthText(v) {
    return v.assemblyLengthMm ? `${v.assemblyLengthMm}mm` : '';
}

/**
 * Human-readable field-assembly name. Always includes the literal
 * phrase "Field Assembly Fast Connector" so the product line is
 * unambiguous in the catalog.
 *
 * Pattern:
 *   <Connector> [<Model>] Field Assembly Fast Connector
 *     [– <Mechanism> Type] : <End/Polish> : <Mode> : <Length>
 *
 * Examples:
 *   SC ESC250D Field Assembly Fast Connector : SC/APC : SM : 52.0mm
 *   SC Field Assembly Fast Connector – Lever Lock Type : SC/UPC : SM : 53.0mm
 *   LC Field Assembly Fast Connector – Mini Push-Lock Type : LC/UPC : SM : 50.0mm
 */
function buildFieldAssemblyName(v) {
    const bits = [];

    // 1. Head connector
    const headType = v.connectorTypeA || 'SC';
    bits.push(headType);

    // 2. Optional model/family prefix — only when it's a specific model
    const family = (v.assemblyFamily || '').trim();
    const isGenericFamily =
        !family || /^Field Assembly Fast Connector$/i.test(family);
    if (!isGenericFamily) {
        bits.push(family);
    }

    // 3. Always include the canonical product line name
    bits.push('Field Assembly Fast Connector');

    // 4. Optional mechanism suffix — skip "Embedded" (it's the default)
    const mech =
        v.assemblyMechanism && v.assemblyMechanism !== 'Embedded'
            ? v.assemblyMechanism
            : '';
    if (mech) {
        bits.push(`– ${mech} Type`);
    }

    // 5. Specs tail
    const specs = [];
    const endSpec = endLabel(v.connectorTypeA, v.polishTypeA);
    if (endSpec) specs.push(endSpec);

    const mode = modeShort(v);
    if (mode) specs.push(mode);

    const len = assemblyLengthText(v);
    if (len) specs.push(len);

    const tail = specs.length ? ` : ${specs.join(' : ')}` : '';
    return bits.join(' ') + tail;
}

// ─── Schema ──────────────────────────────────────────────────────────────
export default {
    slug: 'accessory',
    label: 'Connectors / Adapters / Pigtails / Patch Cords',
    defaultUnit: 'pcs',
    costDefaults: {
        avgVolumeCbm: 0.0001,
        suggestedMarkupPct: 40,
        freightSensitivity: 'low',
        note: 'Connectors/pigtails: use package mode for accuracy',
    },

    groups: [
        {
            id: 'basic',
            label: 'Basic',
            columns: 3,
            fields: [
                {
                    key: 'accessoryType',
                    label: 'Type',
                    type: 'select',
                    required: true,
                    options: ACCESSORY_TYPES,
                },
                {
                    key: 'coreType',
                    label: 'Core Type',
                    type: 'select',
                    options: ['Simplex', 'Duplex', 'Quad'],
                    hint: 'Number of fibers per connector pair',
                },
                {
                    key: 'fiberMode',
                    label: 'Fiber Mode',
                    type: 'select',
                    required: true,
                    options: [
                        { value: 'Singlemode', label: 'Singlemode (SM)' },
                        { value: 'Multimode OM1', label: 'Multimode OM1' },
                        { value: 'Multimode OM2', label: 'Multimode OM2' },
                        { value: 'Multimode OM3', label: 'Multimode OM3' },
                        { value: 'Multimode OM4', label: 'Multimode OM4' },
                        { value: 'Multimode OM5', label: 'Multimode OM5' },
                    ],
                },
            ],
        },

        {
            id: 'fiberSpec',
            label: 'Fiber Specification',
            columns: 2,
            fields: [
                {
                    key: 'fiberStandard',
                    label: 'Fiber Standard (ITU-T / TIA)',
                    type: 'select',
                    required: true,
                    options: FIBER_STANDARDS,
                    hint: 'The grade of fiber inside the cable/connector',
                },
                {
                    key: 'fiberManufacturer',
                    label: 'Fiber Manufacturer / Product',
                    type: 'text',
                    placeholder: 'e.g. Corning ClearCurve ZBL',
                    hint: 'Optional — the raw glass supplier if known',
                },
            ],
        },

        {
            id: 'sideA',
            label: 'Side A (End 1)',
            columns: 2,
            fields: [
                {
                    key: 'connectorTypeA',
                    label: 'Connector',
                    type: 'select',
                    required: true,
                    options: CONNECTOR_OPTIONS,
                },
                {
                    key: 'polishTypeA',
                    label: 'Polish',
                    type: 'select',
                    options: POLISH_OPTIONS,
                },
            ],
        },

        {
            id: 'sideB',
            label: 'Side B (End 2)',
            columns: 2,
            fields: [
                {
                    key: 'connectorTypeB',
                    label: 'Connector',
                    type: 'select',
                    options: [...CONNECTOR_OPTIONS, PIGTAIL_VALUE],
                    hint:
                        'Leave blank for no Side B. ' +
                        'Pick "Pigtail (no end)" for a single-ended pigtail.',
                },
                {
                    key: 'polishTypeB',
                    label: 'Polish',
                    type: 'select',
                    options: POLISH_OPTIONS,
                },
            ],
        },

        {
            id: 'fieldAssembly',
            label: 'Field Assembly Fast Connector',
            columns: 3,
            when: (v) => isFieldAssembly(v),
            fields: [
                {
                    key: 'assemblyFamily',
                    label: 'Model / Family',
                    type: 'select',
                    options: ASSEMBLY_FAMILY_OPTIONS,
                    hint:
                        'Pick the model code, or leave "Field Assembly Fast Connector" for the generic product.',
                },
                {
                    key: 'assemblyMechanism',
                    label: 'Assembly Mechanism',
                    type: 'select',
                    options: ASSEMBLY_MECHANISM_OPTIONS,
                },
                {
                    key: 'assemblyLengthMm',
                    label: 'Connector Body Length',
                    type: 'select',
                    options: ASSEMBLY_LENGTH_OPTIONS,
                    hint: 'Length of the connector body, in millimeters',
                },
            ],
        },

        {
            id: 'physical',
            label: 'Physical & Optical',
            columns: 3,
            fields: [
                {
                    key: 'lengthMeters',
                    label: 'Length',
                    type: 'number',
                    unit: 'm',
                    step: 0.1,
                    min: 0,
                    when: (v) => !isFieldAssembly(v),
                },
                {
                    key: 'diameter_mm',
                    label: 'Cable Diameter',
                    type: 'number',
                    unit: 'mm',
                    step: 0.1,
                },
                {
                    key: 'jacket',
                    label: 'Jacket',
                    type: 'select',
                    options: ['LSZH', 'PVC', 'OFNR', 'Plenum'],
                },
                {
                    key: 'insertionLoss',
                    label: 'Insertion Loss',
                    type: 'number',
                    unit: 'dB',
                    step: 0.01,
                },
                {
                    key: 'returnLoss',
                    label: 'Return Loss',
                    type: 'number',
                    unit: 'dB',
                    step: 0.01,
                },
                {
                    key: 'packSize',
                    label: 'Pack Size',
                    type: 'number',
                    unit: 'pcs',
                    min: 0,
                },
            ],
        },
    ],

    buildSku(v) {
        v = v || {};
        const typeCode = ACCESSORY_TYPE_CODES[v.accessoryType] || 'ACC';
        const mode = modeShort(v);
        const std = v.fiberStandard ? v.fiberStandard.replace(/[.]/g, '') : '';
        const core = CORE_TYPE_CODES[v.coreType] || '';

        const sideA = [v.connectorTypeA, v.polishTypeA]
            .filter(Boolean)
            .join('-');

        const sideB = hasSideBConnector(v)
            ? [v.connectorTypeB, v.polishTypeB].filter(Boolean).join('-')
            : '';
        const pigtail = isSideBPigtail(v) ? 'PIGT' : '';

        let assemblySegments = [];
        if (isFieldAssembly(v)) {
            const fam = (v.assemblyFamily || 'FAC')
                .replace(/Field Assembly Fast Connector/i, 'FAC')
                .replace(/\s+/g, '')
                .toUpperCase();
            const mech = v.assemblyMechanism
                ? v.assemblyMechanism.replace(/[\s-]+/g, '').toUpperCase()
                : '';
            const len = v.assemblyLengthMm ? `${v.assemblyLengthMm}MM` : '';
            assemblySegments = [fam, mech, len].filter(Boolean);
        }

        const tail = isFieldAssembly(v)
            ? assemblySegments
            : [v.lengthMeters ? `${v.lengthMeters}M` : ''].filter(Boolean);

        const parts = [
            typeCode,
            mode,
            std,
            core,
            sideA,
            sideB,
            pigtail,
            ...tail,
        ].filter(Boolean);

        return parts.join('-').toUpperCase().replace(/\s+/g, '');
    },

    buildName(v) {
        v = v || {};

        if (isFieldAssembly(v)) {
            return buildFieldAssemblyName(v);
        }

        const bits = [];
        const sideALabel = endLabel(v.connectorTypeA, v.polishTypeA);
        const sideBLabel = hasSideBConnector(v)
            ? endLabel(v.connectorTypeB, v.polishTypeB)
            : '';

        if (sideALabel && sideBLabel) {
            bits.push(`${sideALabel} to ${sideBLabel}`);
        } else if (sideALabel && isSideBPigtail(v)) {
            bits.push(`${sideALabel} Pigtail`);
        } else if (sideALabel) {
            bits.push(sideALabel);
        }

        if (v.coreType) bits.push(v.coreType);
        if (v.fiberMode) bits.push(v.fiberMode);
        if (v.fiberStandard) bits.push(v.fiberStandard);

        const typeWord = v.accessoryType || '';
        if (typeWord && typeWord !== 'Pigtail') {
            bits.push(typeWord);
        } else if (typeWord === 'Pigtail' && !isSideBPigtail(v)) {
            bits.push(typeWord);
        }

        if (v.lengthMeters) bits.push(`${v.lengthMeters}m`);
        else if (v.packSize) bits.push(`${v.packSize}pcs/pack`);

        if (v.jacket) bits.push(v.jacket);

        return bits.join(' ').trim();
    },

    attributeSchema: [
        { key: 'accessoryType', label: 'Type' },
        { key: 'coreType', label: 'Core Type' },
        { key: 'fiberMode', label: 'Fiber Mode' },
        { key: 'fiberStandard', label: 'Fiber Standard' },
        { key: 'fiberManufacturer', label: 'Fiber Manufacturer' },
        { key: 'connectorTypeA', label: 'Connector (Side A)' },
        { key: 'polishTypeA', label: 'Polish (Side A)' },
        { key: 'connectorTypeB', label: 'Connector (Side B)' },
        { key: 'polishTypeB', label: 'Polish (Side B)' },
        { key: 'assemblyFamily', label: 'Assembly Family' },
        { key: 'assemblyMechanism', label: 'Assembly Mechanism' },
        { key: 'assemblyLengthMm', label: 'Connector Body Length', unit: 'mm' },
        { key: 'lengthMeters', label: 'Length', unit: 'm' },
        { key: 'diameter_mm', label: 'Cable Diameter', unit: 'mm' },
        { key: 'jacket', label: 'Jacket' },
        { key: 'insertionLoss', label: 'Insertion Loss', unit: 'dB' },
        { key: 'returnLoss', label: 'Return Loss', unit: 'dB' },
        { key: 'packSize', label: 'Pack Size' },
    ],
};